import { Header } from '../components/Header';
import { Funds } from '../components/Funds';
import { useForceUpdateReport } from '../../../services/report.service';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  useSyncExternalStore
} from 'react';
import { reaction } from 'mobx';
import { useDebouncedValue, useElementSize } from '@mantine/hooks';
import { useConvertReport, usePeriodById } from '../../../services/period.service';
import { useGetGroupedWorkflows, useGetWorkflowById } from '../../../services/workflow.services';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { usePDF } from 'react-to-pdf';
import { FCACredentialsModal } from '../components/FCACredentialsModal';
import { TableSections } from '../components/TableSections';
import { UpdateReportModal } from '../components/UpdateReportModal';
import { useGetFundsByPeriodId } from '../../../services/funds.service';
import { FaRegSave } from 'react-icons/fa';
import { REPORT_TYPE_IDS } from '../consts/reportTypes';
import { AIF002Reports } from './components/ReportTypes/AIF002';
import {
  sections,
  condensedSections,
  sectionsAIF001,
  sectionsAIF001Condensed,
  tableSections
} from './consts/tableSections';
import { useReport } from './hooks/useReport';
import { useFCA } from '../hooks/useFCA';
import { useReportSectionNavigation } from '../hooks/useReportSectionNavigation';
import SubmitReportModal from '../components/SubmitReportModal';
import { AIF001Reports } from './components/ReportTypes/AIF001';
import { TbSparkles, TbSquareRoundedArrowLeft } from 'react-icons/tb';
import commonStore from '../../../store/common';
import { ReportTypeTabs } from '../../../components/ReportTypeTabs';
import { DrillDown } from '../components/DrillDown';
import { ExportDrawer } from '../components/ExportDrawer';
import { statusKeys } from '../components/Funds/consts';
import { checkToRedirect, checkToRedirectFileUpload } from '../../../utils/checkToRedirect';
import { useUserGroupsCurrentUserAdminState } from '../../../services/user.service';
import { checkIsLockedAutomation } from '../../../utils/lockAutomation';
import reportStore from '../store';
import { permissionPolicies } from '../../../permissions/policies';
import { Box, Button, Flex, Loader, Notification, ScrollArea, Text, Tooltip } from '@mantine/core';
import ReportCommentsSidebar from '../components/ReportComments/ReportCommentsSidebar';
import { REPORT_NAMINGS, REPORT_TYPES } from '../../../consts/reportTypes';
import authStore from '../../../store/auth';
import { extractApiError } from '../../../utils';
import { FEATURE_FLAG_NAMES, useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { REPORT_TYPE_LABELS } from './consts';
import { useWorkflowChrome } from '../../../components/Layout/WorkflowChromeContext';
import { useReportSignalRSync } from '../hooks/useReportSignalRSync';
import { useSignalRListener } from '../../../hooks/useSignalR';
import { SIGNALR_LISTENERS } from '../../../config/signalr';
import { getVisibleFcaReportSections } from './utils/visibleSections';
import { SECTION_ERROR_SCOPE } from './consts/sectionErrorScope';

const ReportContainer = forwardRef(
  (
    {
      isStaticTemplate = false,
      onOpen,
      hideTables = false,
      isMultiFunds,
      handleChangeIsMultiFunds,
      instanceId = 'main'
    },
    ref
  ) => {
    const containerIdPrefix = `fca-report-container-${instanceId}`;
    const { workflowId, periodId } = useParams();

    const { ref: boxRef, width: boxWidth } = useElementSize();

    const [debouncedBoxWidth] = useDebouncedValue(boxWidth, 1500);

    const isDrillDownOpen = useSyncExternalStore(
      onStoreChange => reaction(() => !!commonStore.drillDown, onStoreChange),
      () => !!commonStore.drillDown
    );

    useEffect(() => {
      if (debouncedBoxWidth) commonStore.setContentWidth(debouncedBoxWidth - 124);
    }, [debouncedBoxWidth]);

    const commentsSidebarWidth = useSyncExternalStore(
      onStoreChange => reaction(() => commonStore.commentsSidebarWidth, onStoreChange),
      () => commonStore.commentsSidebarWidth
    );

    const drillDownWidth = useSyncExternalStore(
      onStoreChange => reaction(() => commonStore.drillDownWidth, onStoreChange),
      () => commonStore.drillDownWidth
    );

    const [searchParams, setSearchParams] = useSearchParams();
    const [isOpenUpdateReport, setIsOpenUpdateReport] = useState(false);

    const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
    const [isErrorMsgShown, setIsErrorMsgShown] = useState(false);
    // Last dismissed report-load error — see the ESMA container for the pattern.
    const [dismissedError, setDismissedError] = useState(null);

    const jurisdictionId = searchParams.get('jurisdictionId');
    const commentSectionId = searchParams.get('threadId');

    const groupedWorkflows = useGetGroupedWorkflows({
      id: workflowId,
      queryParams: {
        enabled: !!workflowId && !!jurisdictionId
      }
    });

    const project = useGetWorkflowById({
      id: workflowId,
      queryParams: {
        enabled: !!workflowId && !jurisdictionId,
        onSuccess: res => {
          authStore.setWorkflow(res.id, res.name);
        }
      }
    });

    const { data: currentUser } = useUserGroupsCurrentUserAdminState();

    const canManageWorkflow = permissionPolicies.checkAccess({
      currentUserRole: currentUser?.role,
      currentUserId: currentUser?.azure_object_id,
      assignedTo: project?.data?.assigned_to
    });

    const { isLockedPeriod, setIsLockedPeriod, setIsConversionReportType, setIsComparisonReport } =
      reportStore;

    const period = usePeriodById({
      periodId,
      customKey: 'FCA_PERIOD_BY_ID',
      queryOptions: {
        enabled: !!periodId && !jurisdictionId,
        onSuccess: res => {
          setIsErrorMsgShown(
            [
              res.modeling_status,
              res.transformation_progress_status,
              res.extraction_progress_status,
              res.jurisdiction_validation_progress_status
            ].includes('In progress') && res.report_progress_status === 'In progress'
          );

          return res;
        },
        cacheTime: 0,
        staleTime: 0
      }
    });

    // Refetch the period record when the backend fires a report-related
    // SignalR event so the "In progress" gate on Regenerate Report + the
    // header status readouts flip without a manual refresh.
    useReportSignalRSync({
      periodId,
      workflowId,
      onReportEvent: () => {
        period.refetch?.();
        // Also pull the report body — the period-status refetch flips the header
        // gate, but rows served by GET /api/report/{periodId} stay stale otherwise.
        refetch?.();
      }
    });

    const showComparisonReport =
      period?.data?.template_type !== 'sratch' && project?.data?.periods?.length > 1;

    const isLastPeriod = project?.data?.periods?.[0]?.id === periodId;

    useEffect(() => {
      if (!period?.data || !currentUser) return;

      setIsLockedPeriod(
        checkIsLockedAutomation(period?.data, currentUser) ||
          (!isLastPeriod && currentUser?.role !== 'Admin')
      );
    }, [period?.data, currentUser, isLastPeriod]);

    const fundId = searchParams.get('fund_id');

    const forceUpdateReport = useForceUpdateReport();
    const isAIReportReviewEnabled = useFeatureFlag(FEATURE_FLAG_NAMES.AI_REPORT_REVIEW);

    const { targetRef } = usePDF({ filename: 'page.pdf' });
    const selectedReportType = searchParams.get('type') || REPORT_TYPE_IDS.AIF002;
    const isAIF001 = selectedReportType === REPORT_TYPE_IDS.AIF001;

    const {
      onCreateFCA,
      onDeleteFCA,
      deletingFCA,
      updatingFCA,
      creatingFCA,
      hasErrorOnFCA,
      fcaCredentials,
      isOpenFCACredentials,
      setIsOpenFCACredentials
    } = useFCA({ workflowId, enabled: !!currentUser && currentUser.role !== 'Approver' });

    const {
      funds,
      currentDataKey,
      onSave,
      schema,
      refetch,
      isLoading,
      hasChanges,
      addRow,
      onDeleteRow,
      isLoadingData,
      onChangeFund,
      onChangeDataKey,
      onClearStaticData,
      isLoadingSchema,
      jurisdictions,
      onNavigateOtherReport,
      onChangeSelectedReportType,
      isViewer,
      canRedo,
      canUndo,
      undo,
      redo,
      isLoadingUser,
      allFunds,
      onChangeSelectedFunds,
      withUnsavedChangesGuard,
      reportLoadError,
      isReportLoadError
    } = useReport({
      fundId,
      isStaticTemplate,
      period,
      periodId,
      setSearchParams,
      isAIF001,
      isMultiFunds,
      isLockedPeriod
    });

    const visibleReportSections = useMemo(
      () =>
        getVisibleFcaReportSections({
          tableSections,
          sections,
          condensedSections,
          currentFund: funds[currentDataKey],
          funds: Object.values(funds),
          isMultiFunds,
          isAIF001
        }),
      [currentDataKey, funds, isAIF001, isMultiFunds]
    );

    const {
      virtuosoRef,
      sectionRefs,
      activeSection,
      onNavigateToSection,
      onChangeActiveSection,
      onReportScrollingChange
    } = useReportSectionNavigation({
      tableSections: visibleReportSections.tableSections,
      sections: visibleReportSections.sections,
      sectionsAIF001,
      isAIF001,
      isStaticTemplate
    });

    const inputFunds = useGetFundsByPeriodId({
      periodId: periodId,
      queryParams: {
        enabled: !jurisdictionId
      }
    });

    const isConversionReportType = project?.data?.report_type === REPORT_TYPES.CONVERSION;

    useEffect(() => {
      setIsConversionReportType(isConversionReportType);
    }, [isConversionReportType]);

    useEffect(() => {
      return () => {
        setIsComparisonReport(false);
        commonStore.setDrillDown(null);
      };
    }, []);

    const links = useMemo(() => {
      if (jurisdictionId) {
        const _period = groupedWorkflows?.data?.periods?.find(item => item.id === periodId);
        return [
          {
            name: `${REPORT_NAMINGS.PROJECT?.[project?.data?.report_type]}s`,
            value: '/project'
          },
          {
            name: groupedWorkflows.data?.name,
            value: checkToRedirect({ ...groupedWorkflows.data, is_workflow_group: true })
          },
          {
            name: `Year - ${_period?.year}, ${_period?.period}`
          },
          {
            name: 'Report',
            isCurrent: true
          }
        ];
      }
      const projectLink = checkToRedirect(project.data);

      const baseLinks = [
        { name: `${REPORT_NAMINGS.PROJECT[project?.data?.report_type]}s`, value: '/project' },
        { name: project.data?.name, value: projectLink }
      ];

      if (projectLink.includes('quaters')) {
        return [
          ...baseLinks,
          isConversionReportType
            ? {}
            : {
                name: 'Source Files',
                value: checkToRedirectFileUpload(project?.data, period?.data?.id)
              },
          isConversionReportType
            ? {}
            : {
                name: 'Extraction & Transformation',
                value: `/data-extraction/${project.data?.id}/${period.data?.id}`
              },
          isConversionReportType
            ? {}
            : {
                name: 'Output Table',
                value: `/output-tables/${project.data?.id}/${period.data?.id}`
              },
          {
            name: 'Report',
            isCurrent: true
          }
        ].filter(el => el.name);
      }

      return [
        ...baseLinks,
        isConversionReportType
          ? {}
          : {
              name: 'Extraction & Transformation',
              value: `/data-extraction/${project.data?.id}/${period.data?.id}`
            },
        isConversionReportType
          ? {}
          : {
              name: 'Output Table',
              value: `/output-tables/${project.data?.id}/${period.data?.id}`
            },
        {
          name: 'Report',
          isCurrent: true
        }
      ].filter(el => el.name);
    }, [project.data, period.data, groupedWorkflows.data, jurisdictionId, isConversionReportType]);

    useImperativeHandle(ref, () => ({
      refetchAllData() {
        refetch();
      }
    }));

    // Mirrors the extraction page's SignalR wiring: refetch the report
    // payload + period status directly when AutomationReportTaskUpdate
    // arrives, instead of round-tripping through the chrome's period query.
    const handleReportStatusUpdate = useCallback(
      data => {
        if (data?.period_id && data.period_id !== periodId) return;
        period.refetch();
        refetch();
      },
      [periodId, period, refetch]
    );

    useSignalRListener(
      SIGNALR_LISTENERS.ON_AUTOMATION_REPORT_STATUS_UPDATED,
      handleReportStatusUpdate
    );

    useEffect(() => {
      if (commentSectionId) {
        onNavigateToSection(commentSectionId);
      }
    }, [commentSectionId]);

    const parseConversion = useConvertReport();

    const onOpenUpdateReport = async () => {
      if (isConversionReportType) {
        parseConversion.mutate(
          { periodId },
          {
            onSuccess: async () => {
              await period.refetch();
              refetch();
            },
            onError: err => {
              toast.error(extractApiError(err, 'Unable to parse this report.'));
            }
          }
        );
        return;
      }
      await period.refetch();
      setIsOpenUpdateReport(true);
    };

    const guardedOpenSubmit = () => withUnsavedChangesGuard(() => setIsOpenFCACredentials(true));
    const guardedOpenExport = () =>
      withUnsavedChangesGuard(() => commonStore.setIsOpenExport(true));

    const onCloseUpdateReport = () => {
      setIsOpenUpdateReport(false);
    };

    const onUpdateReport = (fund_id_list, fn, errorFn) => {
      forceUpdateReport.mutate(
        {
          periodId,
          body: {
            fund_id_list
          }
        },
        {
          onSuccess: () => {
            onCloseUpdateReport();
            period.refetch();
            refetch();
            fn();
            toast.success('Report has been updated');
          },
          onError: err => {
            errorFn?.();
            toast.error(extractApiError(err, 'Unable to update this report.'), {
              duration: 3500
            });
          }
        }
      );
    };

    const [isExpandedQuickNavigation, setIsExpandedQuickNavigation] = useState(true);

    const toggleQuickNavigation = () => {
      setIsExpandedQuickNavigation(prev => !prev);
    };

    const fundsWithStatus = useMemo(() => {
      if (funds) {
        let errors = Object.values(funds).filter(
          item => item.report_status?.submit_status === statusKeys.ERROR
        );

        let submited = Object.values(funds).filter(
          item => item.report_status?.submit_status === statusKeys.SUBMITTED
        );

        let readyForSubmit = Object.values(funds).filter(
          item => item.report_status?.submit_status === statusKeys.READY
        );

        return {
          errors,
          submited,
          readyForSubmit
        };
      }

      return {
        errors: [],
        submited: [],
        readyForSubmit: []
      };
    }, [funds]);

    const navigate = useNavigate();

    // Register Report as the active stepper stage. The page-level chrome
    // (AppTopBar + WorkflowStepper + WorkflowToolbar) renders above the
    // inline <Header>; the Header still owns the breadcrumb + Approver/Export
    // drawers (including its own ?openApprovalDrawer=true sentinel handler).
    const { setLabels, clearLabels } = useWorkflowChrome();
    useEffect(() => {
      if (isStaticTemplate) return;
      setLabels({
        projectLabel: project.data?.name,
        periodLabel:
          period.data?.year && period.data?.period
            ? `${period.data.year}, ${period.data.period}`
            : undefined,
        showComments: true,
        workflowId,
        periodId,
        activeStageKey: 'report',

        backStepHandler:
          isConversionReportType && currentUser?.role === 'Approver'
            ? () => navigate('/project')
            : undefined
      });
      return () => clearLabels();
    }, [
      isStaticTemplate,
      project.data?.name,
      period.data?.year,
      period.data?.period,
      workflowId,
      periodId,
      isConversionReportType,
      currentUser?.role,
      navigate,
      setLabels,
      clearLabels
    ]);

    // Stepper's Submission step deep-links here with `openSubmitDrawer=true`.
    // Open the submit flow (FCA Credentials → SubmitReportModal) and strip
    // the sentinel so a refresh doesn't reopen the modal.
    useEffect(() => {
      if (searchParams.get('openSubmitDrawer') !== 'true') return;
      setIsOpenFCACredentials(true);
      const next = new URLSearchParams(searchParams);
      next.delete('openSubmitDrawer');
      setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams, setIsOpenFCACredentials]);

    if (isLoadingUser) return <div />;

    const shouldShowLoadError = isReportLoadError && reportLoadError !== dismissedError;
    const shouldShowUpdateNotification =
      !isStaticTemplate &&
      isErrorMsgShown &&
      !!currentUser &&
      currentUser.role !== 'Approver' &&
      !shouldShowLoadError;

    return (
      <>
        {!isStaticTemplate && (
          <Header
            id={`${containerIdPrefix}-header`}
            onOpen={onOpen}
            onOpenUpdateReport={onOpenUpdateReport}
            isAIF001={isAIF001}
            links={links}
            isLoadingProject={project.isLoading}
            isViewer={isViewer}
            isMultiFunds={isMultiFunds}
            isRefetchingPeriod={period.isRefetching || parseConversion.isLoading}
            isReportInProgress={period.data?.is_force_update_in_progress === true}
            onBeforeAction={withUnsavedChangesGuard}
            assignedTo={project?.data?.assigned_to}
            extraActions={
              isAIReportReviewEnabled ? (
                <Tooltip label="Available for ESMA reports" withArrow>
                  <Box component="span">
                    <Button
                      id={`${containerIdPrefix}-ai-review-button`}
                      variant="outline"
                      size="sm"
                      leftSection={<TbSparkles />}
                      disabled
                      title="Available for ESMA reports"
                      styles={{
                        root: {
                          backgroundColor: '#e7f4ff',
                          borderColor: '#228be6',
                          color: '#1c7ed6',
                          fontWeight: 600
                        }
                      }}
                    >
                      AI Review
                    </Button>
                  </Box>
                </Tooltip>
              ) : null
            }
          />
        )}

        <Box
          id={`${containerIdPrefix}-shell`}
          bg="#f8f9fa"
          p="12px 16px 16px"
          sx={() => ({
            height: isStaticTemplate ? 'calc(100% - 62px)' : 'auto'
          })}
        >
          {!isStaticTemplate && (
            <ReportTypeTabs
              onChangeSelectedReportType={onChangeSelectedReportType}
              selectedReportType={selectedReportType}
              handleChangeIsMultiFunds={handleChangeIsMultiFunds}
              isMultiFunds={isMultiFunds}
              onSaveReport={onSave}
              jurisdictions={jurisdictions}
              reportTypes={REPORT_TYPE_LABELS}
              isAIF001={isAIF001}
              onNavigateOtherReport={onNavigateOtherReport}
              isViewer={isViewer}
              canManageWorkflow={canManageWorkflow}
              canRedo={canRedo}
              canUndo={canUndo}
              undo={undo}
              redo={redo}
              funds={allFunds}
              onChangeSelectedFunds={onChangeSelectedFunds}
              showComparisonReport={showComparisonReport}
              hasChanges={hasChanges}
              onBeforeAction={withUnsavedChangesGuard}
              onOpenExport={guardedOpenExport}
              onOpenSubmit={guardedOpenSubmit}
            />
          )}

          <Flex
            id={`${containerIdPrefix}-workspace`}
            gap="12px"
            mt="12px"
            h={isStaticTemplate ? '98%' : 'auto'}
          >
            {!hideTables && (
              <Box id={`${containerIdPrefix}-sidebar-wrapper`} pos="relative">
                <ScrollArea
                  scrollbarSize={14}
                  scrollHideDelay={500}
                  viewportProps={{ id: `${containerIdPrefix}-scrollable-sidebar` }}
                  p="24px 12px"
                  bg="white"
                  h={isStaticTemplate ? '100%' : 'calc(100vh - 338px)'}
                  sx={() => ({
                    position: 'sticky',
                    top: '16px',
                    minWidth: !isExpandedQuickNavigation ? '68px' : '272px',
                    maxWidth: !isExpandedQuickNavigation ? '68px' : '272px',
                    borderRadius: '4px',
                    border: '1px solid #f1f3f5',
                    overflowY: 'auto',
                    transition: '0.4s ease-in-out all'
                  })}
                >
                  <Flex
                    id={`${containerIdPrefix}-sidebar-header`}
                    align="center"
                    justify="space-between"
                    mb="16px"
                    p="0 8px"
                  >
                    <Text
                      id={`${containerIdPrefix}-sidebar-title`}
                      sx={() => ({
                        transition: isExpandedQuickNavigation
                          ? '2s ease-in-out opacity'
                          : '0.2s ease-in-out opacity',
                        opacity: isExpandedQuickNavigation ? 1 : 0,
                        width: isExpandedQuickNavigation ? 'auto' : 0,
                        height: isExpandedQuickNavigation ? 'auto' : 0,
                        overflow: 'hidden'
                      })}
                      fz="16px"
                      lh="22px"
                      c="gray.7"
                    >
                      Quick Navigation
                    </Text>
                    <button
                      id={`${containerIdPrefix}-sidebar-toggle`}
                      display="flex"
                      onClick={toggleQuickNavigation}
                      style={{
                        transform: !isExpandedQuickNavigation && 'rotateZ(180deg)'
                      }}
                    >
                      <TbSquareRoundedArrowLeft color="#DEE2E6" fontSize="20px" />
                    </button>
                  </Flex>
                  <TableSections
                    funds={isMultiFunds ? Object.values(funds) : [funds[currentDataKey]]}
                    isStaticTemplate={isStaticTemplate}
                    sections={isAIF001 ? sectionsAIF001 : visibleReportSections.sections}
                    condensedSections={
                      isAIF001 ? sectionsAIF001Condensed : visibleReportSections.condensedSections
                    }
                    isExpanded={isExpandedQuickNavigation}
                    onNavigateToSection={onNavigateToSection}
                    activeSection={activeSection}
                    schema={schema}
                    isViewer={isViewer}
                    sectionErrorScope={SECTION_ERROR_SCOPE}
                  />
                </ScrollArea>
              </Box>
            )}

            <Box
              id={
                isStaticTemplate ? `${containerIdPrefix}-content-2` : `${containerIdPrefix}-content`
              }
              p="16px"
              ref={boxRef}
              className="report-table"
              sx={() => ({
                flex: 1,
                borderRadius: '4px',
                border: '1px solid #f1f3f5',
                backgroundColor: '#fff',
                height: isStaticTemplate ? '100%' : 'calc(100vh - 338px)',
                overflow: 'auto',
                marginRight: isDrillDownOpen ? drillDownWidth - commentsSidebarWidth : 0,
                transition: 'margin-right 200ms ease'
              })}
            >
              {Object.keys(funds).length !== 0 && (
                <Funds
                  currentDataKey={currentDataKey}
                  onChangeDataKey={onChangeDataKey}
                  funds={funds}
                  isStaticTemplate={isStaticTemplate}
                  onClearStaticData={onClearStaticData}
                  isAIF001={isAIF001}
                  canRedo={canRedo}
                  canUndo={canUndo}
                  undo={undo}
                  redo={redo}
                  handleChangeIsMultiFunds={handleChangeIsMultiFunds}
                  isMultiFunds={isMultiFunds}
                  isViewer={isViewer}
                />
              )}

              {(isLoading || isLoadingSchema) && (
                <Flex
                  id={`${containerIdPrefix}-loading-state`}
                  justify="center"
                  align="center"
                  h="100%"
                >
                  <Loader color="blue" />
                </Flex>
              )}

              {!isLoading && !isLoadingSchema && (
                <>
                  {!hideTables &&
                    (isAIF001 ? (
                      <AIF001Reports
                        isStaticTemplate={isStaticTemplate}
                        targetRef={targetRef}
                        currentDataKey={currentDataKey}
                        onChangeFund={onChangeFund}
                        schema={schema}
                        funds={funds}
                        addRow={addRow}
                        onDeleteRow={onDeleteRow}
                        isMultiFunds={false}
                        sectionRefs={sectionRefs}
                      />
                    ) : (
                      <AIF002Reports
                        tableSections={visibleReportSections.tableSections}
                        isStaticTemplate={isStaticTemplate}
                        targetRef={targetRef}
                        currentDataKey={currentDataKey}
                        onChangeFund={onChangeFund}
                        schema={schema}
                        funds={funds}
                        addRow={addRow}
                        onDeleteRow={onDeleteRow}
                        isMultiFunds={isMultiFunds}
                        sectionRefs={sectionRefs}
                        onChangeActiveSection={onChangeActiveSection}
                        onScrollingChange={onReportScrollingChange}
                        virtuosoRef={virtuosoRef}
                        isViewer={isViewer}
                      />
                    ))}
                </>
              )}
            </Box>

            <ReportCommentsSidebar
              id={`${containerIdPrefix}-comments-sidebar`}
              isMultiFunds={isMultiFunds}
              onNavigateToSection={onNavigateToSection}
              isStaticTemplate={isStaticTemplate}
            />
          </Flex>
          {shouldShowUpdateNotification && (
            <Box
              id={`${containerIdPrefix}-notification-wrapper`}
              pos="absolute"
              sx={() => ({
                zIndex: 9999
              })}
              bottom="20px"
              w="400px"
              left="calc(50% - 200px)"
            >
              <Notification
                id={`${containerIdPrefix}-notification`}
                onClose={() => setIsErrorMsgShown(prev => !prev)}
                withCloseButton
              >
                {`The source of report has been updated. Please update the report ${isAIF001 ? 'AIF002 ' : ''}(click
                "Regenerate Report") to sync with latest changes. Your previous report
                will be overwritten.`}
              </Notification>
            </Box>
          )}
          {shouldShowLoadError && (
            <Box
              id={`${containerIdPrefix}-load-error-wrapper`}
              pos="absolute"
              sx={() => ({ zIndex: 9999 })}
              bottom="20px"
              w="440px"
              left="calc(50% - 220px)"
            >
              <Notification
                id={`${containerIdPrefix}-load-error`}
                color="red"
                title="Couldn't load the report"
                withCloseButton
                onClose={() => setDismissedError(reportLoadError)}
              >
                {extractApiError(
                  reportLoadError,
                  'Failed to get all data. Please try again or contact support.'
                )}
              </Notification>
            </Box>
          )}
        </Box>

        <UpdateReportModal
          id={`${containerIdPrefix}-update-modal`}
          isLoading={forceUpdateReport.isLoading}
          onUpdateReport={onUpdateReport}
          inputFunds={inputFunds}
          isOpen={isOpenUpdateReport}
          onClose={onCloseUpdateReport}
          workflowId={workflowId}
          periodId={periodId}
          transformationStatus={period?.data?.transformation_progress_status}
          extractionStatus={period?.data?.extraction_progress_status}
          lookupStatus={period?.data?.lookup_progress_status}
        />

        <FCACredentialsModal
          id={`${containerIdPrefix}-fca-modal`}
          key={isOpenFCACredentials}
          isOpen={isOpenFCACredentials}
          onClose={() => setIsOpenFCACredentials(false)}
          onSubmit={onCreateFCA}
          onDelete={onDeleteFCA}
          isLoadingDelete={deletingFCA}
          isLoading={creatingFCA || updatingFCA}
          credentials={hasErrorOnFCA ? null : fcaCredentials}
        />

        <SubmitReportModal
          id={`${containerIdPrefix}-submit-modal`}
          isOpen={isSubmitModalOpen}
          onClose={() => setIsSubmitModalOpen(false)}
          periodId={periodId}
          funds={funds ? Object.values(funds) : []}
          refetchFunds={refetch}
        />

        {isStaticTemplate && !isLockedPeriod && (
          <Flex
            id={`${containerIdPrefix}-footer`}
            justify="flex-end"
            p="16px"
            pos="absolute"
            left={0}
            right={0}
            bottom={0}
            bg="white"
            sx={() => ({
              zIndex: 999
            })}
          >
            <Button
              id={`${containerIdPrefix}-save-button`}
              loading={isLoadingData}
              onClick={onSave}
              size="xs"
              leftSection={<FaRegSave />}
            >
              Save
            </Button>
          </Flex>
        )}

        <DrillDown id={`${containerIdPrefix}-drilldown`} />
        <ExportDrawer
          id={`${containerIdPrefix}-export-drawer`}
          jurisdictions={jurisdictions}
          periodId={periodId}
          hasWarning={fundsWithStatus.errors.length > 0}
          workflowId={workflowId}
        />
      </>
    );
  }
);

export default ReportContainer;
